import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { acceptInvite } from '../api/invites';
import { storeToken } from '../api/auth';
import { useAuth } from '../auth/AuthContext';

const AcceptInvitePage: React.FC = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError('Invalid invite link.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await acceptInvite(token, password, passwordConfirmation);
      storeToken(response.token);
      await refresh();
      navigate('/', { replace: true });
    } catch (_err: any) {
      setError('Invalid invite or credentials.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, width: 420 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Accept invite
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          Set your password to activate your invite-only account.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="new-password"
          />
          <TextField
            label="Confirm Password"
            type="password"
            value={passwordConfirmation}
            onChange={(event) => setPasswordConfirmation(event.target.value)}
            required
            autoComplete="new-password"
          />
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Activate Account'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AcceptInvitePage;
